import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from '@heroicons/react/20/solid';

const testimonials = [
  {
    id: 1,
    name: 'Dazzle Healer',
    role: 'Front End Developer',
    text: "I've used other kits, but this one is the best. The attention to detail and usability are truly amazing for all designers. I highly recommend it for any type of project.",
    avatar: 'https://i.pravatar.cc/150?img=11',
  },
  {
    id: 2,
    name: 'Crystal Maiden',
    role: 'UIUX Designer',
    text: "This UI Kit is incredibly helpful for my designs. The layouts and illustrations are clean, modern, and very easy to customize. It's perfect for beginners and professionals.",
    avatar: 'https://i.pravatar.cc/150?img=5',
  },
  {
    id: 3,
    name: 'Mirana Marci',
    role: '3D Designer',
    text: "This UI Kit saved me hours of work. It's intuitive, high-quality, and totally worth the price for all design needs. My projects look more professional and appealing now.",
    avatar: 'https://i.pravatar.cc/150?img=12',
  },
  {
    id: 4,
    name: 'Bimosaurus',
    role: 'Graphic Designer',
    text: "Amazing work! The color schemes are vibrant, and the icons fit perfectly with all my projects, especially modern UI designs. It makes everything look polished and user-friendly instantly.",
    avatar: 'https://i.pravatar.cc/150?img=9',
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { type: 'spring', stiffness: 80, damping: 20 } 
  },
};

const TestimonialSection = () => {
  return (
    <section className="bg-[#fafafc] py-16 relative overflow-hidden font-sans">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-2xl text-center mb-10"
        >
          <h2 className="text-[13px] font-bold tracking-wide text-[#1e50ff] mb-3 uppercase">Client Feedback</h2>
          <p className="text-3xl font-extrabold tracking-tight text-[#111827] sm:text-[2rem]">
            What They Say After Using Our Product
          </p>
        </motion.div>
        
        {/* Testimonials Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="mx-auto grid max-w-2xl grid-cols-1 gap-6 md:grid-cols-2 lg:max-w-none lg:grid-cols-2 lg:gap-8 xl:gap-10"
        >
          {testimonials.map((testimonial) => (
            <motion.div 
              key={testimonial.id}
              variants={cardVariants}
              whileHover={{ 
                y: -5,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.08)" 
              }}
              className="flex flex-col rounded-[1.5rem] bg-white p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 border border-transparent hover:border-gray-50 cursor-default"
            >
              {/* Card Header (Avatar + Stars) */}
              <div className="flex items-start justify-between mb-6">
                
                {/* Avatar and Info */}
                <div className="flex items-center gap-x-4">
                  <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                    <img src={testimonial.avatar} alt={testimonial.name} className="h-full w-full object-cover" />
                  </div>
                  <div>
                    <div className="font-bold text-[#111827] text-base leading-tight">{testimonial.name}</div>
                    <div className="text-[13px] font-medium text-gray-500 mt-1">{testimonial.role}</div>
                  </div>
                </div>

                {/* Stars */}
                <div className="flex gap-x-[2px] text-[#f97316] mt-1">
                  {[0, 1, 2, 3, 4].map((star) => (
                    <StarIcon key={star} className="h-[18px] w-[18px] flex-none" aria-hidden="true" />
                  ))}
                </div>

              </div>
              
              {/* Testimonial Text */}
              <p className="text-[15px] leading-[1.7] text-gray-600 font-medium tracking-tight">
                {testimonial.text}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* View All Button */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 flex justify-center"
        >
          <button className="rounded-full bg-[#1e50ff] px-8 py-3.5 text-[15px] font-bold text-white hover:bg-[#163fdb] hover:shadow-lg hover:shadow-blue-500/30 hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1e50ff] transition-all duration-300 flex items-center gap-2">
            View All
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4 mt-[1px]">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialSection;
